import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
    Loader2, Search, Building2, Landmark, User,
    Database, X, Eye, ExternalLink, CheckCircle2, AlertTriangle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import SortableHeader, { TablePagination } from '../table/SortableHeader';
import ImportDiffDialog from './ImportDiffDialog';
import BatchImportDialog from './BatchImportDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_PAGE_SIZE = 25;
const MAX_BATCH = 50;

const ENTITY_TABS = [
    { key: 'company',  label: 'Companies', Icon: Building2 },
    { key: 'investor', label: 'Investors', Icon: Landmark },
    { key: 'person',   label: 'People',    Icon: User },
];

const CRM_FILTERS = [
    { key: '',           label: 'All' },
    { key: 'linked',     label: 'In CRM' },
    { key: 'not_linked', label: 'Not imported' },
];

// ── Column registries ────────────────────────────────────────────────────────
// Keys match the CRM target field names emitted in item.props by the backend.
// Companies + Investors share COMPANY_COLUMNS (both project onto account records).
// People tab uses CONTACT_COLUMNS (projects onto contact records).

const COMPANY_COLUMNS = [
    { key: 'city',                    label: 'City',               group: 'company' },
    { key: 'state',                   label: 'State/Province',     group: 'company' },
    { key: 'country',                 label: 'Country',            group: 'company' },
    { key: 'region',                  label: 'Region',             group: 'company' },
    { key: 'website',                 label: 'Website',            group: 'company' },
    { key: 'phone',                   label: 'Phone',              group: 'company' },
    { key: 'email',                   label: 'Email',              group: 'company' },
    { key: 'industry',                label: 'Industry',           group: 'company' },
    { key: 'industry_group',          label: 'Industry Group',     group: 'company' },
    { key: 'investor_type',           label: 'Investor Type',      group: 'company' },
    { key: 'ownership_status',        label: 'Ownership Status',   group: 'company' },
    { key: 'financing_status',        label: 'Financing Status',   group: 'company' },
    { key: 'business_status',         label: 'Business Status',    group: 'company' },
    { key: 'num_employees',           label: 'Employees',          group: 'company' },
    { key: 'founded_year',            label: 'Year Founded',       group: 'company' },
    { key: 'total_raised',            label: 'Total Raised',       group: 'company' },
    { key: 'annual_revenue',          label: 'Revenue',            group: 'company' },
    { key: 'aum',                     label: 'AUM',                group: 'company' },
    { key: 'dry_powder',              label: 'Dry Powder',         group: 'company' },
    { key: 'total_investments',       label: 'Total Investments',  group: 'company' },
    { key: 'total_active_portfolio',  label: 'Active Portfolio',   group: 'company' },
    { key: 'description_short',       label: 'Description',        group: 'company' },
    { key: 'linkedin_url',            label: 'LinkedIn',           group: 'company' },
    { key: 'also_known_as',           label: 'Also Known As',      group: 'company' },
];

const CONTACT_COLUMNS = [
    { key: 'title',                      label: 'Job Title',              group: 'contact' },
    { key: 'seniority',                  label: 'Seniority',              group: 'contact' },
    { key: 'primary_company_name',       label: 'Company',                group: 'contact' },
    { key: 'primary_company_industry',   label: 'Company Industry',       group: 'contact' },
    { key: 'primary_company_type',       label: 'Company Type',           group: 'contact' },
    { key: 'email',                      label: 'Email',                  group: 'contact' },
    { key: 'phone',                      label: 'Phone',                  group: 'contact' },
    { key: 'mobile',                     label: 'Mobile',                 group: 'contact' },
    { key: 'linkedin_url',               label: 'LinkedIn',               group: 'contact' },
    { key: 'education',                  label: 'Education',              group: 'contact' },
    { key: 'birth_year',                 label: 'Birth Year',             group: 'contact' },
    { key: 'gender',                     label: 'Gender',                 group: 'contact' },
    { key: 'bio',                         label: 'Biography',              group: 'contact' },
];

// Default visible sets — reproduce today's bare view plus a few useful extras.
// Split by entity so each tab opens with data that is actually present.
const DEFAULT_VISIBLE_COMPANY = new Set([
    'city', 'country', 'website', 'industry', 'ownership_status',
]);
const DEFAULT_VISIBLE_INVESTOR = new Set([
    'city', 'country', 'website', 'investor_type', 'total_investments',
]);
const DEFAULT_VISIBLE_PERSON = new Set([
    'title', 'primary_company_name', 'email',
]);

const FILTERABLE_COLUMNS_BY_ENTITY = {
    company: new Set([
        'country', 'state', 'region', 'industry', 'industry_group',
        'ownership_status', 'financing_status', 'business_status',
    ]),
    investor: new Set(['country', 'state', 'region', 'investor_type', 'business_status']),
    person: new Set(['seniority']),
};

const SORTABLE_COLUMNS_BY_ENTITY = {
    company: new Set([
        'city', 'state', 'country', 'region', 'website', 'phone', 'email',
        'industry', 'industry_group', 'ownership_status', 'financing_status',
        'business_status', 'description_short', 'linkedin_url', 'also_known_as',
    ]),
    investor: new Set([
        'city', 'state', 'country', 'region', 'website', 'phone', 'email',
        'investor_type', 'business_status', 'description_short', 'linkedin_url',
        'also_known_as',
    ]),
    person: new Set([
        'title', 'seniority', 'primary_company_name', 'primary_company_industry',
        'primary_company_type', 'email', 'phone', 'mobile', 'linkedin_url',
        'education', 'gender', 'bio',
    ]),
};

function defaultVisibleFor(entity) {
    if (entity === 'person') return DEFAULT_VISIBLE_PERSON;
    if (entity === 'investor') return DEFAULT_VISIBLE_INVESTOR;
    return DEFAULT_VISIBLE_COMPANY;
}

// ── Value formatter ──────────────────────────────────────────────────────────
// Money fields are stored as raw USD floats after projector.to_crm_value.
const MONEY_KEYS = new Set([
    'total_raised', 'annual_revenue', 'enterprise_value', 'gross_profit',
    'net_income', 'ebitda', 'ebit', 'net_debt', 'aum', 'dry_powder',
    'median_round_amount', 'median_valuation', 'min_fund_size', 'max_fund_size',
]);

function formatPropValue(key, value) {
    if (value === null || value === undefined || value === '') return '—';
    if (MONEY_KEYS.has(key) && typeof value === 'number') {
        if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`.replace('.0B', 'B');
        if (value >= 1_000_000)     return `$${(value / 1_000_000).toFixed(1)}M`.replace('.0M', 'M');
        if (value >= 1_000)         return `$${(value / 1_000).toFixed(1)}K`.replace('.0K', 'K');
        return `$${value}`;
    }
    if (Array.isArray(value)) return value.join(', ') || '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

function serializeColumnFilters(filters) {
    const payload = {};
    Object.entries(filters || {}).forEach(([key, values]) => {
        const list = Array.from(values || []).filter(Boolean);
        if (list.length > 0) payload[key] = list;
    });
    return Object.keys(payload).length ? JSON.stringify(payload) : '';
}

// ── CRM status badge ─────────────────────────────────────────────────────────

function CrmStatusBadge({ item }) {
    if (item.crm_status === 'linked') {
        const href = item.entity === 'person' ? `/contacts/${item.crm_id}` : `/companies/${item.crm_id}`;
        return (
            <a href={href} target="_blank" rel="noreferrer"
                title={item.crm_name ? `Open ${item.crm_name}` : 'Open CRM record'}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 hover:bg-emerald-100 whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                In CRM <ExternalLink className="w-2.5 h-2.5" />
            </a>
        );
    }
    if (item.crm_status === 'match') {
        return (
            <span title={item.crm_name ? `Matches ${item.crm_name} — an import through List Management updates it, no duplicate` : undefined}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700 ring-1 ring-blue-100 whitespace-nowrap">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                Match in CRM
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-stone-100 text-stone-500 ring-1 ring-stone-200 whitespace-nowrap">
            <span className="w-1.5 h-1.5 rounded-full bg-stone-400" />
            Not in CRM
        </span>
    );
}

function OwnerCell({ item }) {
    if (item.owner_name) {
        return (
            <span className="inline-flex items-center gap-1 text-blue-700">
                <CheckCircle2 className="w-3 h-3" />
                {item.owner_name}
            </span>
        );
    }
    if (item.crm_status === 'linked' || item.crm_status === 'match') {
        return (
            <span className="inline-flex items-center gap-1 text-amber-600">
                <AlertTriangle className="w-3 h-3" />
                In CRM - unassigned
            </span>
        );
    }
    return <span className="text-stone-400">—</span>;
}

// ── Component ────────────────────────────────────────────────────────────────

/**
 * Browse the indexed PitchBook replica. "Preview" opens a read-only comparison
 * with the CRM; importing happens in List Management (#767), which the preview
 * dialogs link to.
 */
export default function PitchBookDataBrowser({ refreshKey = 0 }) {
    const { getAuthHeader } = useAuth();
    const [entity, setEntity] = useState('company');
    const [search, setSearch] = useState('');
    const [crmFilter, setCrmFilter] = useState('');
    const [skip, setSkip] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [previewTarget, setPreviewTarget] = useState(null);
    const [batchOpen, setBatchOpen] = useState(false);
    const debounceRef = useRef(null);

    // Column picker state — in-session only, resets on entity switch.
    const [visibleColumns, setVisibleColumns] = useState(() => defaultVisibleFor('company'));
    const [showColumnPicker, setShowColumnPicker] = useState(false);
    const [sortBy, setSortBy] = useState('');
    const [sortDir, setSortDir] = useState('asc');
    const [columnFilters, setColumnFilters] = useState({});
    const [facetOptions, setFacetOptions] = useState({});
    const [facetMeta, setFacetMeta] = useState({});
    const [facetLoading, setFacetLoading] = useState({});

    // Selection: { [pitchbook_id]: { name, entity } } — survives pagination / search.
    // Only populated for company/investor tabs (people preview via their company).
    const [selection, setSelection] = useState({});
    const selectionCount = Object.keys(selection).length;

    const filtersQuery = useMemo(() => serializeColumnFilters(columnFilters), [columnFilters]);

    const load = useCallback(async (params) => {
        setLoading(true);
        try {
            const queryParams = {
                limit: DEFAULT_PAGE_SIZE,
                ...params,
            };
            if (queryParams.sortBy) {
                queryParams.sort_by = queryParams.sortBy;
                queryParams.sort_dir = queryParams.sortDir || 'asc';
            }
            delete queryParams.sortBy;
            delete queryParams.sortDir;
            if (queryParams.filtersQuery) queryParams.filters = queryParams.filtersQuery;
            delete queryParams.filtersQuery;
            const res = await axios.get(`${API}/pitchbook/browse`, {
                params: queryParams,
                headers: getAuthHeader(),
            });
            setData(res.data);
        } catch {
            setData({ indexed: false, items: [], totals: {}, has_more: false });
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader]);

    const browseParams = useCallback((overrides = {}) => ({
        entity,
        q: search,
        skip,
        crm: crmFilter,
        limit: pageSize,
        sortBy,
        sortDir,
        filtersQuery,
        ...overrides,
    }), [entity, search, skip, crmFilter, pageSize, sortBy, sortDir, filtersQuery]);


    // Reload on tab/filter/page changes + external refresh (e.g. after an index run).
    useEffect(() => {
        load(browseParams());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entity, skip, crmFilter, pageSize, sortBy, sortDir, filtersQuery, refreshKey, load]);

    // Debounced search.
    const onSearchChange = (value) => {
        setSearch(value);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setSkip(0);
            load(browseParams({ q: value, skip: 0 }));
        }, 350);
    };
    useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

    const switchEntity = (key) => {
        setEntity(key);
        setSkip(0);
        setCrmFilter('');
        setSortBy('');
        setSortDir('asc');
        setColumnFilters({});
        setFacetOptions({});
        setFacetMeta({});
        setFacetLoading({});
        setVisibleColumns(defaultVisibleFor(key));
        setShowColumnPicker(false);
        // Keep cross-tab selection intact — admin may want company + investor mix.
    };

    const totals = data?.totals || {};
    const items = useMemo(() => data?.items || [], [data]);
    const matchTotal = data?.match_total ?? totals[entity] ?? 0;
    const page = Math.floor(skip / pageSize) + 1;
    const totalPages = Math.max(1, Math.ceil(matchTotal / pageSize));

    const handlePageChange = (nextPage) => {
        const bounded = Math.min(Math.max(1, nextPage), totalPages);
        setSkip((bounded - 1) * pageSize);
    };

    const handlePageSizeChange = (nextPageSize) => {
        setPageSize(nextPageSize);
        setSkip(0);
    };

    const handleSort = (columnKey) => {
        setSkip(0);
        if (sortBy === columnKey) {
            if (sortDir === 'asc') {
                setSortDir('desc');
            } else {
                setSortBy('');
                setSortDir('asc');
            }
        } else {
            setSortBy(columnKey);
            setSortDir('asc');
        }
    };

    const setColumnFilter = (columnKey, valueSet) => {
        setSkip(0);
        setColumnFilters((prev) => {
            const next = { ...prev };
            if (valueSet && valueSet.size > 0) next[columnKey] = valueSet;
            else delete next[columnKey];
            return next;
        });
    };

    const loadFacetOptions = useCallback(async (columnKey) => {
        const cacheKey = `${entity}:${columnKey}`;
        if (facetOptions[cacheKey] || facetLoading[cacheKey]) return;
        setFacetLoading((prev) => ({ ...prev, [cacheKey]: true }));
        try {
            const res = await axios.get(`${API}/pitchbook/browse/facets`, {
                params: { entity, column: columnKey },
                headers: getAuthHeader(),
            });
            setFacetOptions((prev) => ({ ...prev, [cacheKey]: res.data?.values || [] }));
            setFacetMeta((prev) => ({ ...prev, [cacheKey]: res.data || {} }));
        } catch {
            setFacetOptions((prev) => ({ ...prev, [cacheKey]: [] }));
            setFacetMeta((prev) => ({ ...prev, [cacheKey]: { truncated: false } }));
        } finally {
            setFacetLoading((prev) => ({ ...prev, [cacheKey]: false }));
        }
    }, [entity, facetOptions, facetLoading, getAuthHeader]);

    // IDs visible on the current page (company/investor tabs only).
    const selectableItems = useMemo(
        () => items.filter((i) => i.entity !== 'person'),
        [items]);
    const pageIds = useMemo(() => selectableItems.map((i) => i.pitchbook_id), [selectableItems]);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selection[id]);
    const somePageSelected = pageIds.some((id) => selection[id]);

    const toggleItem = (item) => {
        setSelection((s) => {
            const next = { ...s };
            if (next[item.pitchbook_id]) {
                delete next[item.pitchbook_id];
            } else {
                if (Object.keys(next).length >= MAX_BATCH) return s; // cap
                next[item.pitchbook_id] = { name: item.name, entity: item.entity };
            }
            return next;
        });
    };

    // Header checkbox: if ANY page rows are selected, clicking unselects them;
    // only a fully unselected page selects everything (up to the cap).
    const toggleAllPage = () => {
        if (somePageSelected) {
            setSelection((s) => {
                const next = { ...s };
                pageIds.forEach((id) => delete next[id]);
                return next;
            });
        } else {
            setSelection((s) => {
                const next = { ...s };
                let added = 0;
                for (const item of selectableItems) {
                    if (!next[item.pitchbook_id]) {
                        if (Object.keys(next).length + added >= MAX_BATCH) break;
                        next[item.pitchbook_id] = { name: item.name, entity: item.entity };
                        added++;
                    }
                }
                return next;
            });
        }
    };

    const clearSelection = () => setSelection({});

    const openPreview = (item) => {
        if (item.entity === 'person') {
            if (!item.company_pb_id) return;
            setPreviewTarget({
                pitchbookId: item.company_pb_id,
                recordName: item.company_name || item.name,
                personFocusId: item.pitchbook_id,
            });
        } else {
            setPreviewTarget({ pitchbookId: item.pitchbook_id, recordName: item.name });
        }
    };

    const isPersonTab = entity === 'person';

    // Active column registry and visible subset.
    const columnRegistry = isPersonTab ? CONTACT_COLUMNS : COMPANY_COLUMNS;
    const displayColumns = useMemo(
        () => columnRegistry.filter((c) => visibleColumns.has(c.key)),
        [columnRegistry, visibleColumns]
    );

    // Total column count for empty-state colSpan:
    // checkbox (if not person) + Name + dynamic + Owner + CRM status + actions
    const totalColSpan = (isPersonTab ? 0 : 1) + 1 + displayColumns.length + 1 + 1 + 1;

    return (
        <div className="bg-white">
            {/* ── Tabs + toolbar ── */}
            <div className="flex flex-wrap items-center gap-3 px-4 pt-4 pb-3 border-b border-stone-100">
                <div className="inline-flex p-1 rounded-xl bg-stone-100">
                    {ENTITY_TABS.map(({ key, label, Icon }) => {
                        const active = entity === key;
                        return (
                            <button key={key} onClick={() => switchEntity(key)}
                                className={`flex items-center gap-2 px-3.5 py-1.5 text-sm font-medium rounded-lg transition-all
                                    ${active ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}>
                                <Icon className="w-4 h-4" />{label}
                                <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-semibold
                                    ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-500'}`}>
                                    {(totals[key] ?? 0).toLocaleString()}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <div className="relative flex-1 min-w-[200px] max-w-xs ml-auto">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                    <Input value={search} onChange={(e) => onSearchChange(e.target.value)}
                        placeholder={entity === 'person' ? 'Search people, companies, emails…' : 'Search names…'}
                        className="pl-9 h-8 text-xs" />
                    {search && (
                        <button onClick={() => onSearchChange('')}
                            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                            <X className="w-3.5 h-3.5" />
                        </button>
                    )}
                </div>

                {/* Column picker toggle */}
                <Button variant="outline" size="sm"
                    className={`h-8 text-xs ${showColumnPicker ? 'bg-stone-100' : ''}`}
                    onClick={() => setShowColumnPicker((v) => !v)}>
                    Columns ({visibleColumns.size})
                </Button>

                <div className="inline-flex p-0.5 bg-stone-100 rounded-lg">
                    {CRM_FILTERS.map((f) => (
                        <button key={f.key}
                            onClick={() => { setCrmFilter(f.key); setSkip(0); }}
                            className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-all
                                ${crmFilter === f.key ? 'bg-white shadow-sm text-stone-800' : 'text-stone-400 hover:text-stone-600'}`}>
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Column picker panel ── */}
            {showColumnPicker && (
                <div className="bg-stone-50 border-b border-stone-200 px-4 py-3">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-semibold text-stone-500 uppercase tracking-wide">
                            Visible Columns
                        </span>
                        <div className="flex gap-3">
                            <button
                                className="text-xs text-stone-500 hover:text-stone-700"
                                onClick={() => setVisibleColumns(new Set(columnRegistry.map((c) => c.key)))}>
                                Show All
                            </button>
                            <button
                                className="text-xs text-stone-500 hover:text-stone-700"
                                onClick={() => setVisibleColumns(defaultVisibleFor(entity))}>
                                Reset
                            </button>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        {columnRegistry.map((col) => {
                            const on = visibleColumns.has(col.key);
                            const color = col.group === 'contact'
                                ? on ? 'bg-blue-50 border-blue-200 text-blue-700'   : 'bg-white border-stone-200 text-stone-400'
                                : on ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-white border-stone-200 text-stone-400';
                            return (
                                <button
                                    key={col.key}
                                    onClick={() => {
                                        setVisibleColumns((prev) => {
                                            const next = new Set(prev);
                                            if (next.has(col.key)) next.delete(col.key);
                                            else next.add(col.key);
                                            return next;
                                        });
                                    }}
                                    className={`text-xs px-2 py-1 rounded-full border transition-colors ${color}`}>
                                    {col.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* ── Table ── */}
            {loading && !data ? (
                <div className="py-12 text-center text-stone-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
            ) : data && data.indexed === false ? (
                <div className="py-12 text-center text-stone-500">
                    <Database className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                    <p className="text-sm font-medium">No PitchBook data indexed yet</p>
                    <p className="text-xs text-stone-400 mt-1">New exports are indexed automatically — or index a file by hand from the bucket files dialog.</p>
                </div>
            ) : (
                <>
                    <div className={`overflow-auto max-h-[64vh] transition-opacity ${loading ? 'opacity-50' : ''}`}>
                        <table className="w-full text-xs">
                            <thead className="bg-stone-50 text-stone-500 sticky top-0 z-10 shadow-[0_1px_0_0_rgba(231,229,228,1)]">
                                <tr>
                                    {/* Checkbox header — company/investor only */}
                                    {!isPersonTab && (
                                        <th className="px-3 py-2 w-8">
                                            <input
                                                type="checkbox"
                                                checked={allPageSelected}
                                                ref={(el) => {
                                                    if (el) el.indeterminate = somePageSelected && !allPageSelected;
                                                }}
                                                onChange={toggleAllPage}
                                                className="rounded border-stone-300 text-stone-900 focus:ring-stone-700 cursor-pointer align-middle"
                                                title={somePageSelected ? 'Unselect page' : 'Select page'}
                                            />
                                        </th>
                                    )}
                                    {/* Pinned: Name */}
                                    <SortableHeader
                                        label="Name"
                                        columnKey="name"
                                        sortColumn={sortBy}
                                        sortDirection={sortDir}
                                        onSort={handleSort}
                                        filterable={false}
                                        serverSide
                                        className="!px-4 !py-2 !font-medium !text-left !whitespace-nowrap !normal-case !tracking-normal"
                                    />
                                    {/* Dynamic data columns */}
                                    {displayColumns.map((col) => {
                                        const cacheKey = `${entity}:${col.key}`;
                                        const filterable = FILTERABLE_COLUMNS_BY_ENTITY[entity]?.has(col.key);
                                        const sortable = SORTABLE_COLUMNS_BY_ENTITY[entity]?.has(col.key);
                                        if (!sortable && !filterable) {
                                            return (
                                                <th key={col.key} className="px-4 py-2 font-medium text-left whitespace-nowrap">
                                                    {col.label}
                                                </th>
                                            );
                                        }
                                        return (
                                            <SortableHeader
                                                key={col.key}
                                                label={col.label}
                                                columnKey={col.key}
                                                sortColumn={sortBy}
                                                sortDirection={sortDir}
                                                onSort={sortable ? handleSort : () => {}}
                                                filterValue={columnFilters[col.key]}
                                                onFilter={filterable ? setColumnFilter : undefined}
                                                filterable={filterable}
                                                serverSide
                                                options={facetOptions[cacheKey] || []}
                                                onFilterOpen={filterable ? loadFacetOptions : undefined}
                                                loadingOptions={!!facetLoading[cacheKey]}
                                                optionsTruncated={!!facetMeta[cacheKey]?.truncated}
                                                className="!px-4 !py-2 !font-medium !text-left !whitespace-nowrap !normal-case !tracking-normal"
                                            />
                                        );
                                    })}
                                    {/* Pinned: Owner + CRM status + actions */}
                                    <th className="px-4 py-2 font-medium text-left whitespace-nowrap">Owner</th>
                                    <th className="px-4 py-2 font-medium text-left whitespace-nowrap">CRM status</th>
                                    <th className="px-4 py-2 font-medium text-right whitespace-nowrap" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-100">
                                {items.length === 0 && !loading && (
                                    <tr>
                                        <td colSpan={totalColSpan} className="px-4 py-8 text-center text-stone-400">
                                            No records match.
                                        </td>
                                    </tr>
                                )}
                                {items.map((item) => {
                                    const isSelected = !!selection[item.pitchbook_id];
                                    const atCap = selectionCount >= MAX_BATCH && !isSelected;
                                    const props = item.props ?? {};
                                    return (
                                        <tr key={item.pitchbook_id}
                                            className={`hover:bg-stone-50 ${isSelected ? 'bg-stone-100/60' : ''}`}>
                                            {/* Checkbox — company/investor only */}
                                            {!isPersonTab && (
                                                <td className="px-3 py-2.5">
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        disabled={atCap}
                                                        onChange={() => toggleItem(item)}
                                                        className={`rounded border-stone-300 text-stone-900 focus:ring-stone-700 align-middle ${atCap ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}
                                                        title={atCap ? `Maximum ${MAX_BATCH} records` : undefined}
                                                    />
                                                </td>
                                            )}
                                            {/* Pinned: Name / identity */}
                                            <td className="px-4 py-2.5">
                                                <p className="font-medium text-stone-800 truncate max-w-[260px]" title={item.name}>{item.name || '—'}</p>
                                                <p className="text-[10px] text-stone-400 font-mono">{item.pitchbook_id}</p>
                                            </td>
                                            {/* Dynamic data columns */}
                                            {displayColumns.map((col) => {
                                                // Prefer props (full mapped set); fall back to slim key on item.
                                                const raw = props[col.key] !== undefined ? props[col.key] : item[col.key];
                                                const display = formatPropValue(col.key, raw);
                                                return (
                                                    <td key={col.key}
                                                        className="px-4 py-2.5 text-stone-600 truncate max-w-[180px]"
                                                        title={typeof raw === 'string' ? raw : undefined}>
                                                        {display}
                                                    </td>
                                                );
                                            })}
                                            {/* Pinned: Owner */}
                                            <td className="px-4 py-2.5 text-stone-600 whitespace-nowrap">
                                                <OwnerCell item={item} />
                                            </td>
                                            {/* Pinned: CRM status */}
                                            <td className="px-4 py-2.5"><CrmStatusBadge item={item} /></td>
                                            {/* Pinned: read-only Preview action */}
                                            <td className="px-4 py-2.5 text-right">
                                                <Button size="sm" variant="outline" className="h-7 text-[11px]"
                                                    data-testid="pb-row-preview"
                                                    disabled={item.entity === 'person' && !item.company_pb_id}
                                                    title={item.entity === 'person' && !item.company_pb_id
                                                        ? 'No primary company in the indexed data'
                                                        : 'Compare with your CRM — importing happens in List Management'}
                                                    onClick={() => openPreview(item)}>
                                                    <Eye className="w-3 h-3 mr-1" />
                                                    Preview
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <TablePagination
                        page={page}
                        totalPages={totalPages}
                        total={matchTotal}
                        pageSize={pageSize}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        pageSizeOptions={[25, 50, 100]}
                        alwaysShow
                        disabled={loading}
                    />
                </>
            )}

            {/* Floating bulk-action bar — portaled to <body> so position:fixed
                tracks the viewport (a transformed ancestor would otherwise
                trap it inside this component). */}
            {selectionCount > 0 && !batchOpen && createPortal((
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-8 pl-6 pr-3 py-2.5 bg-white border border-stone-200 rounded-full shadow-lg shadow-stone-200/60">
                    <div className="flex items-center gap-2 text-xs text-stone-800 whitespace-nowrap">
                        <span className="font-medium">{selectionCount} selected</span>
                        {selectionCount >= MAX_BATCH && (
                            <span className="text-stone-400">(max {MAX_BATCH})</span>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button onClick={clearSelection}
                            className="text-xs text-stone-500 hover:text-stone-800 px-3 py-1.5 rounded-full hover:bg-stone-100">
                            Clear
                        </button>
                        <Button size="sm" className="h-9 px-5 text-xs rounded-full"
                            data-testid="pb-batch-preview"
                            onClick={() => setBatchOpen(true)}>
                            Preview {selectionCount} selected
                        </Button>
                    </div>
                </div>
            ), document.body)}

            {previewTarget && (
                <ImportDiffDialog
                    pitchbookId={previewTarget.pitchbookId}
                    recordName={previewTarget.recordName}
                    personFocusId={previewTarget.personFocusId || null}
                    onClose={() => setPreviewTarget(null)}
                />
            )}

            {batchOpen && (
                <BatchImportDialog
                    pitchbookIds={Object.keys(selection)}
                    selectionMeta={selection}
                    onClose={() => setBatchOpen(false)}
                />
            )}
        </div>
    );
}
